import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  TrendingUp, LayoutDashboard, Wallet, Server, Users, BarChart3,
  ArrowDownToLine, History, User, LogOut, Menu, X, ChevronRight,
  AlertCircle, Trophy, PlusCircle, Bell, Bitcoin, MessageCircle
} from 'lucide-react';

const navItems = [
  { path: '/dashboard',       icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/wallet',          icon: Wallet,          label: 'Wallet' },
  { path: '/wallet-recharge', icon: PlusCircle,      label: 'Add Funds' },
  { path: '/mt5',             icon: Server,          label: 'MT5 Account' },
  { path: '/referral',        icon: Users,           label: 'My Team' },
  { path: '/commission',      icon: BarChart3,       label: 'Transactions' },
  { path: '/support-tickets', icon: MessageCircle,   label: 'Support Tickets' },
  { path: '/achievements',    icon: Trophy,          label: 'Ranks & Rewards' },
  { path: '/invest',          icon: Bitcoin,         label: 'Invest' },
  { path: '/withdrawal',      icon: ArrowDownToLine, label: 'Withdrawal' },
  { path: '/profit-history',  icon: History,         label: 'Profit History' },
  { path: '/profile',         icon: User,            label: 'Profile' },
];

export default function UserLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [showNotif, setShowNotif] = useState(false);
  const [offerConfig, setOfferConfig] = useState(null);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    fetchNotifications();
    fetchOfferConfig();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchOfferConfig = async () => {
    try {
      const { data } = await axios.get('/api/payment/admin-payment-config');
      if (data?.offerEnabled) {
        setOfferConfig(data);
        setShowOfferModal(true);
      }
    } catch (e) {
      // ignore if popup config not available
    }
  };

  const fetchNotifications = async () => {
    try {
      const { data } = await axios.get('/api/notifications');
      setNotifications(data.notifications || []);
      setUnread(data.unreadCount || 0);
    } catch (e) {}
  };

  const markAllRead = async () => {
    await axios.put('/api/notifications/read-all').catch(() => {});
    setUnread(0);
    setNotifications(n => n.map(x => ({ ...x, isRead: true })));
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  const Sidebar = ({ mobile = false }) => (
    <div className={`${mobile ? 'flex flex-col h-full' : 'hidden lg:flex flex-col h-screen sticky top-0'} w-64 bg-dark-800 border-r border-white/5`}>
      <div className="p-6 border-b border-white/5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-primary-500 rounded-lg flex items-center justify-center flex-shrink-0">
            <TrendingUp className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="font-bold text-white">UniqueAiTarde</p>
            <p className="text-xs text-primary-400">MT5 Trading</p>
          </div>
        </div>
      </div>

      <div className="p-4 border-b border-white/5">
        <div className="bg-dark-600 rounded-xl p-3">
          <p className="text-white font-medium text-sm truncate">{user?.name}</p>
          <p className="text-gray-400 text-xs truncate">{user?.email}</p>
          <div className="flex items-center gap-2 mt-2">
            <span className={`w-2 h-2 rounded-full ${user?.isActive ? 'bg-primary-500' : 'bg-red-500'}`}></span>
            <span className="text-xs text-gray-400">{user?.isActive ? 'Active' : 'Inactive'}</span>
            <span className="ml-auto text-xs bg-primary-500/20 text-primary-400 px-2 py-0.5 rounded-full">
              Lvl {user?.level || 0}
            </span>
          </div>
        </div>
      </div>

      {!user?.setupFeePaid && (
        <div className="mx-4 mt-3">
          <Link to="/setup-fee"
            className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 text-amber-400 text-xs hover:bg-amber-500/20 transition-colors">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            Pay subcription Fee to Activate
          </Link>
        </div>
      )}

      {user?.isActive && user?.mainWalletBalance <= 100 && user?.setupFeeApproved && (
        <div className="mx-4 mt-2">
          <Link to="/wallet-recharge"
            className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-red-400 text-xs hover:bg-red-500/20 transition-colors">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            Low Wallet — Recharge Now
          </Link>
        </div>
      )}

      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {navItems.map(({ path, icon: Icon, label }) => {
          const active = location.pathname === path;
          return (
            <Link key={path} to={path} onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150
                ${active ? 'bg-primary-600 text-white' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
              <Icon className="w-5 h-5 flex-shrink-0" />
              <span className="flex-1">{label}</span>
              {active && <ChevronRight className="w-4 h-4" />}
            </Link>
          );
        })}
      </nav>

      <div className="p-4 border-t border-white/5">
        <button onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-all">
          <LogOut className="w-5 h-5" />
          Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-dark-900 flex">
      <Sidebar />

      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => setSidebarOpen(false)} />
          <div className="relative z-10 w-64 h-full"><Sidebar mobile /></div>
        </div>
      )}

      <div className="flex-1 min-w-0">
        <div className="sticky top-0 z-40 bg-dark-800 border-b border-white/5 px-4 py-3 flex items-center justify-between">
          <button onClick={() => setSidebarOpen(true)} className="text-gray-400 hover:text-white lg:hidden">
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-2 lg:hidden">
            <TrendingUp className="w-5 h-5 text-primary-500" />
            <span className="font-bold text-white">UniqueAiTarde</span>
          </div>
          <div className="hidden lg:block" />

          <div className="relative">
            <button
              onClick={() => { setShowNotif(!showNotif); if (unread > 0) markAllRead(); }}
              className="relative p-2 text-gray-400 hover:text-white rounded-xl hover:bg-white/5 transition-colors">
              <Bell className="w-5 h-5" />
              {unread > 0 && (
                <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                  {unread > 9 ? '9+' : unread}
                </span>
              )}
            </button>

            {showNotif && (
              <div className="absolute right-0 top-12 w-80 bg-dark-700 border border-white/10 rounded-2xl shadow-2xl z-50 overflow-hidden">
                <div className="p-4 border-b border-white/5 flex items-center justify-between">
                  <p className="text-white font-semibold">Notifications</p>
                  <button onClick={() => setShowNotif(false)} className="text-gray-400 hover:text-white">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="max-h-80 overflow-y-auto divide-y divide-white/5">
                  {notifications.length === 0 ? (
                    <p className="text-center text-gray-500 py-8 text-sm">No notifications</p>
                  ) : notifications.map(n => (
                    <div key={n._id} className={`px-4 py-3 ${!n.isRead ? 'bg-primary-500/5' : ''}`}>
                      <p className="text-white text-sm font-medium">{n.title}</p>
                      <p className="text-gray-400 text-xs mt-0.5 leading-relaxed">{n.message}</p>
                      <p className="text-gray-600 text-xs mt-1">
                        {new Date(n.createdAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {showNotif && <div className="fixed inset-0 z-30" onClick={() => setShowNotif(false)} />}
        {offerConfig && showOfferModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
            <div className="w-full max-w-2xl bg-dark-700 border border-white/10 rounded-3xl overflow-hidden shadow-2xl">
              <div className="flex items-start justify-between gap-4 p-5 border-b border-white/10">
                <div>
                  <h2 className="text-xl font-semibold text-white">Special Offer</h2>
                  <p className="text-gray-400 text-sm mt-1">This offer is shown when you log in or refresh.</p>
                </div>
                <button onClick={() => setShowOfferModal(false)}
                  className="text-gray-400 hover:text-white bg-white/5 rounded-full p-2 transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-5">
                {offerConfig.offerText ? (
                  <p className="text-gray-200 text-sm whitespace-pre-line">{offerConfig.offerText}</p>
                ) : (
                  <p className="text-gray-400 text-sm">No offer text configured yet.</p>
                )}
                {offerConfig.offerImage && (
                  <div className="rounded-3xl overflow-hidden border border-white/10 bg-dark-600">
                    <img src={offerConfig.offerImage} alt="Offer" className="w-full h-auto object-contain" />
                  </div>
                )}
              </div>
              <div className="p-5 border-t border-white/10 text-right">
                <button onClick={() => setShowOfferModal(false)}
                  className="bg-primary-600 hover:bg-primary-500 text-white rounded-2xl px-5 py-2.5 font-semibold transition-colors">
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
        <main className="p-4 lg:p-8 max-w-7xl mx-auto">{children}</main>
      </div>
    </div>
  );
}
